import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

import type { TransportMode } from './carriers'

const api = clientFor('/mtm/api')

/**
 * 计划状态机（spec §4.1）：draft → confirmed → in_transit → completed；draft/confirmed → cancelled。
 * 非法流转由后端挡成 409，前端只按这张表控制按钮显隐。
 */
export type PlanStatus = 'draft' | 'confirmed' | 'in_transit' | 'completed' | 'cancelled'

/** 区段状态：pending 待执行、done 已完成、skipped 跳过 */
export type SegmentStatus = 'pending' | 'done' | 'skipped'

/** 计划级动作，与后端 POST /plans/:id/confirm|cancel 及编辑入口一一对应 */
export type PlanAction = 'edit' | 'confirm' | 'cancel'

/** 计划头。字段与 services/mtm/internal/plan/model.go 的 Plan 一一对应。 */
export interface Plan {
  id: number
  tenant_id: number
  /** 计划单号，前缀 YL */
  doc_no: string
  cargo_desc: string
  weight_kg: number
  origin: string
  dest: string
  /** 由服务端按区段 SUM(distance_km) 算出，入参里没有，只读展示 */
  total_distance_km: number
  /** 由服务端按区段 SUM(cost) 算出，入参里没有，只读展示 */
  est_cost: number
  status: PlanStatus
  remark: string
  created_at: string
  updated_at: string
}

/** 列表行：计划 + 区段总数与已完成数（进度条数据源） */
export interface PlanRow extends Plan {
  segment_count: number
  done_count: number
}

/** 区段：某承运商用某种方式从一个场站到另一个场站。 */
export interface Segment {
  id: number
  tenant_id: number
  plan_id: number
  /** 从 1 连续，服务端按数组顺序重排 */
  seq: number
  mode: TransportMode
  carrier_id: number
  from_hub_id: number
  to_hub_id: number
  depart_at: string | null
  arrive_at: string | null
  distance_km: number
  cost: number
  status: SegmentStatus
  created_at: string
  updated_at: string
}

/** 详情：计划 + 按 seq 升序的区段 */
export interface PlanDetail extends Plan {
  segments: Segment[]
}

/** 新建 / 编辑计划的入参：est_cost 与 total_distance_km 不在其中，服务端按区段 SUM */
export interface PlanInput {
  cargo_desc?: string
  weight_kg: number
  origin: string
  dest: string
  remark?: string
}

/**
 * 整体替换区段的入参。seq 与 status 不上传：
 * seq 由服务端按数组顺序从 1 连续重排，status 一律落 pending。
 * 承运商与两端场站后端必填（0 会被 422 挡下），表单校验先行；未选时不进 body。
 */
export interface SegmentInput {
  mode: TransportMode
  carrier_id?: number
  from_hub_id?: number
  to_hub_id?: number
  depart_at?: string
  arrive_at?: string
  distance_km: number
  cost: number
}

export interface PlanQuery extends PageQuery {
  keyword?: string
  status?: string
}

/** 计划表单态：空文本字段用 '' 表示没填 */
export interface PlanForm {
  cargo_desc: string
  weight_kg: number
  origin: string
  dest: string
  remark: string
}

/** 区段表单态：数字 id 用 undefined 表示未选，时间用 null 表示没填 */
export interface SegmentForm {
  mode: TransportMode | ''
  carrier_id?: number
  from_hub_id?: number
  to_hub_id?: number
  depart_at: string | null
  arrive_at: string | null
  distance_km: number
  cost: number
}

/** 与后端 services/mtm/internal/plan/model.go 的 StatusNames 同源两份 */
export const STATUS_LABELS: Record<PlanStatus, string> = {
  draft: '草稿',
  confirmed: '已确认',
  in_transit: '在途',
  completed: '已完成',
  cancelled: '已取消',
}

/** 与后端 services/mtm/internal/plan/model.go 的 SegStatusNames 同源两份 */
export const SEG_STATUS_LABELS: Record<SegmentStatus, string> = {
  pending: '待执行',
  done: '已完成',
  skipped: '已跳过',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<PlanStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> = {
  draft: 'info',
  confirmed: 'primary',
  in_transit: 'warning',
  completed: 'success',
  cancelled: 'danger',
}

/** 区段状态 → 标签色 */
export const SEG_STATUS_TAG: Record<SegmentStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> = {
  pending: 'info',
  done: 'success',
  skipped: 'warning',
}

/**
 * 各状态下允许的计划级动作，抄后端 internal/plan/service.go 的状态机：
 * draft 能编辑/确认/取消，confirmed 只能取消，之后三个状态只读。
 */
const PLAN_ACTIONS: Record<PlanStatus, PlanAction[]> = {
  draft: ['edit', 'confirm', 'cancel'],
  confirmed: ['cancel'],
  in_transit: [],
  completed: [],
  cancelled: [],
}

/** 计划级动作能不能出现（前端只做展示，最终以后端 409 为准） */
export function canAct(status: PlanStatus, action: PlanAction): boolean {
  return PLAN_ACTIONS[status].includes(action)
}

/** 逐段完成：后端只放行 confirmed / in_transit，全部 done 自动 completed */
export function canDoneSegment(status: PlanStatus): boolean {
  return status === 'confirmed' || status === 'in_transit'
}

/** 表单 → 请求体：空字段不进 body（undefined 在 JSON.stringify 里会被丢掉） */
export function planInput(form: PlanForm): PlanInput {
  const input: PlanInput = {
    weight_kg: Number(form.weight_kg) || 0,
    origin: form.origin.trim(),
    dest: form.dest.trim(),
  }
  const cargoDesc = form.cargo_desc.trim()
  if (cargoDesc) input.cargo_desc = cargoDesc
  const remark = form.remark.trim()
  if (remark) input.remark = remark
  return input
}

/**
 * 区段表单 → 请求体：seq 不上传（服务端按顺序重排），
 * 未选的承运商 / 场站 id 与没填的时间都留在 undefined，不进 body。
 */
export function segmentInputs(rows: SegmentForm[]): SegmentInput[] {
  return rows.map((row) => {
    const input: SegmentInput = {
      mode: (row.mode || 'truck') as TransportMode,
      distance_km: Number(row.distance_km) || 0,
      cost: Number(row.cost) || 0,
    }
    if (row.carrier_id) input.carrier_id = row.carrier_id
    if (row.from_hub_id) input.from_hub_id = row.from_hub_id
    if (row.to_hub_id) input.to_hub_id = row.to_hub_id
    if (row.depart_at) input.depart_at = row.depart_at
    if (row.arrive_at) input.arrive_at = row.arrive_at
    return input
  })
}

export const listPlans = (q: PlanQuery): Promise<PageResult<PlanRow>> =>
  api.get<PageResult<PlanRow>>('/plans', { ...q })

export const getPlan = (id: number): Promise<PlanDetail> => api.get<PlanDetail>(`/plans/${id}`)

export const createPlan = (input: PlanInput): Promise<Plan> => api.post<Plan>('/plans', input)

export const updatePlan = (id: number, input: PlanInput): Promise<Plan> =>
  api.put<Plan>(`/plans/${id}`, input)

export const confirmPlan = (id: number): Promise<PlanDetail> =>
  api.post<PlanDetail>(`/plans/${id}/confirm`, {})

export const cancelPlan = (id: number): Promise<PlanDetail> =>
  api.post<PlanDetail>(`/plans/${id}/cancel`, {})

/** 整体替换 draft 计划的区段：请求体是裸数组，服务端按顺序重排 seq 并重算汇总 */
export const replaceSegments = (id: number, segments: SegmentInput[]): Promise<PlanDetail> =>
  api.post<PlanDetail>(`/plans/${id}/segments`, segments)

/** 逐段完成：全部 done 时后端把计划自动置为 completed */
export const doneSegment = (id: number, seq: number): Promise<PlanDetail> =>
  api.post<PlanDetail>(`/plans/${id}/segments/${seq}/done`, {})
